import { IGeoJson2DGeographicCoordinates, LocationType } from '~/client/graph'
import { nearestByDistance } from '~/client/src/shared/utils/nearestByDistance'

export interface IPinSnapCandidate {
  id: string
  coordinates: IGeoJson2DGeographicCoordinates | null
}

/**
 * Which Location a dropped or moved workflow pin fills.
 *
 * The pin's map view decides first: the nearest allowed location it places and shows, then its LBS
 * tag, and only then the project-wide nearest. Every entry point — seed, pin moves and the precise
 * location picker — must agree, and a project-wide nearest that the map view hides is exactly the
 * wrong answer, which is why the order is here with tests rather than inline in each caller.
 */
export interface IPinSnapContext {
  /** Placed AND shown on the pin's map view, at that view's own placement — never gated by the camera. */
  globeItems: IPinSnapCandidate[]
  /** The map view's LBS tag (a ProjectLocation tag counts); null with no map view or no tag. */
  lbsTagLocationId: string | null
  /** Every location at its project-wide placement: the last resort. */
  projectItems: IPinSnapCandidate[]
  /** Resolved by the caller, which owns the field, the workflow's companies, type and dates. */
  isAllowed: (locationId: string) => boolean
}

// The first of a map view's assigned locations is its LBS tag; the map and the snap read it alike.
export const globeLbsTag = (
  assigned:
    | readonly { id: string; is: (type: LocationType) => boolean }[]
    | null
    | undefined,
): { isProjectTagged: boolean; lbsTagLocationId: string | null } => ({
  isProjectTagged: !!assigned?.some(l => l.is(LocationType.ProjectLocation)),
  lbsTagLocationId: assigned?.[0]?.id ?? null,
})

export const resolvePinSnapLocationId = (
  ctx: IPinSnapContext,
  pin: { latitude: number; longitude: number },
): string | null => {
  const nearestAllowed = (items: IPinSnapCandidate[]) =>
    nearestByDistance(
      items.filter(i => ctx.isAllowed(i.id)),
      pin,
      i => i.coordinates,
    )?.id ?? null
  return (
    nearestAllowed(ctx.globeItems) ??
    (ctx.lbsTagLocationId && ctx.isAllowed(ctx.lbsTagLocationId)
      ? ctx.lbsTagLocationId
      : null) ??
    nearestAllowed(ctx.projectItems)
  )
}
